import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API ||
  "";

const IMAGE_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_IMAGE_BASE_URL) ||
  process.env.REACT_APP_IMAGE ||
  process.env.REACT_APP_ASSET_BASE_URL ||
  "";

const PLACEHOLDER = "/images/placeholders/category.png";

const joinImageURL = (base, path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path) || /^\/\//.test(path)) return path;
  const cleanBase = String(base || "").replace(/\/+$/, "");
  const cleanPath = String(path).replace(/^\/+/, "");
  return cleanBase ? `${cleanBase}/${cleanPath}` : `/${cleanPath}`;
};

const pickCategoryImage = (cat) => {
  const pick = (v) => (typeof v === "string" ? v : v?.url || v?.src || "");
  const raw =
    pick(cat?.image) ||
    pick(cat?.image_url) ||
    pick(cat?.logo) ||
    pick(cat?.thumbnail) ||
    pick(cat?.icon) ||
    pick(cat?.cover);
  return raw ? joinImageURL(IMAGE_BASE, raw) : PLACEHOLDER;
};

export default function ProductList() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/category`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (alive) {
          setCats(Array.isArray(json) ? json : []);
          setErr("");
        }
      } catch (e) {
        if (alive) setErr("Gagal memuat kategori.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="relative">
      <div className="pt-6 px-4 sm:px-6 md:px-8 max-w-7xl mx-auto">

        {loading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-2xl border bg-white/70 backdrop-blur p-4 sm:p-6 shadow-sm"
              >
                <div className="h-20 sm:h-24 bg-gray-200 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="p-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
            {err}
          </div>
        )}

        {!loading && !err && cats.length === 0 && (
          <div className="p-4 text-[#7C7C7C] bg-gray-50 rounded-xl border">
            Belum ada kategori.
          </div>
        )}

        {!loading && !err && cats.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
            {cats.map((cat) => {
              const name = cat?.name ?? "Untitled";
              const slug = cat?.slug ?? "";
              const img = pickCategoryImage(cat);

              return (
                <Link
                  key={cat.id ?? slug ?? name}
                  to={`/ctg/${slug}`}
                  state={cat}
                  aria-label={name}
                  title={name}
                  className="
                    group relative rounded-2xl overflow-hidden
                    touch-manipulation active:scale-[0.98]
                    p-px bg-gradient-to-br from-emerald-400/50 via-transparent to-cyan-400/50
                    transition-transform
                  "
                >
                  <div className="rounded-2xl bg-white/90 backdrop-blur shadow-sm hover:shadow-md transition-shadow h-full flex flex-col">
                    
                    <div className="relative aspect-[4/3] grid place-items-center p-4 sm:p-6">
                      <img
                        src={img}
                        alt={name}
                        loading="lazy"
                        decoding="async"
                        className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PLACEHOLDER;
                        }}
                        sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 90vw"
                      />
                      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_50%_30%,rgba(16,185,129,0.10),transparent_60%)]" />
                    </div>

                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 mt-auto">
                      <h3 className="text-[14px] sm:text-[15px] md:text-base font-semibold text-[#0B132A] line-clamp-2">
                        {name}
                      </h3>

                      <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
                        <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                        <span>View Brands</span>
                      </div>
                    </div>

                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
